// Environment types (local definitions since shared/types/common doesn't exist)
export type Environment = 'development' | 'staging' | 'production';

export interface AppConfig {
  environment: Environment;
  apiUrl: string;
  wsUrl?: string;
  version: string;
  debug: boolean;
}

// Environment variable validation and defaults
function getEnvVar(key: string, defaultValue?: string): string {
  const value = import.meta.env[key];
  if (!value && !defaultValue) {
    throw new Error(`Missing required environment variable: ${key}`);
  }
  return value || defaultValue!;
}

function getOptionalEnvVar(key: string, defaultValue?: string): string | undefined {
  return import.meta.env[key] || defaultValue;
}

// App configuration
export const config: AppConfig = {
  environment: (getOptionalEnvVar('VITE_APP_ENV', 'development') as Environment),
  apiUrl: getEnvVar('VITE_API_URL', 'http://localhost:8000'),
  wsUrl: getOptionalEnvVar('VITE_WS_URL'),
  version: getOptionalEnvVar('VITE_APP_VERSION', '1.0.0') || '1.0.0',
  debug: import.meta.env.DEV,
};

// Authentication configuration
export const authConfig = {
  // Disable mock auth - use real backend authentication
  enableMockAuth: false,
  clientEmail: 'demo@bitcoin-newsletter.com',
  clientPassword: 'demo2025',
};

// Validation
if (!['development', 'staging', 'production'].includes(config.environment)) {
  throw new Error(`Invalid environment: ${config.environment}`);
}

// Helper functions
export const isDevelopment = config.environment === 'development';
export const isProduction = config.environment === 'production';
export const isStaging = config.environment === 'staging';

// API endpoints
export const API_ENDPOINTS = {
  // Authentication
  login: '/auth/login',
  logout: '/auth/logout',
  refresh: '/auth/refresh',

  // Articles
  articles: '/api/articles',
  article: (id: number) => `/api/articles/${id}`,

  // Content (Phase 2 - Unified Content API)
  content: '/api/content',
  contentAnalysis: (id: number) => `/api/content/${id}/analysis`,

  // Videos (Direct video endpoints)
  videos: '/api/videos',
  video: (id: number) => `/api/videos/${id}`,

  // Publishers
  publishers: '/api/publishers',

  // Admin
  adminStatus: '/admin/status',
  adminIngest: '/admin/ingest',
  adminTasks: '/admin/tasks',
  adminTaskQueue: '/admin/task-queue',
  adminTaskQueuePriority: '/admin/task-queue/priority-process',
  adminTaskQueueStreamTicket: '/admin/task-queue/stream-ticket',
  adminBatchLane: '/admin/batch-lane',
  adminScheduledTasks: '/admin/scheduled-tasks',
  adminTriggerAbsenceScan: '/admin/tasks/trigger-absence-scan',
  adminScheduledTaskHistory: (taskName: string) => `/admin/scheduled-tasks/${taskName}/history`,

  // System
  health: '/health',
  metrics: '/metrics',
  statsKgQualityIndex: '/api/stats/kg-quality-index',

  // Monitoring
  circuitBreakerStatus: '/monitoring/circuit-breaker/status',
  circuitBreakerReset: '/monitoring/circuit-breaker/reset',

  // Newsletter Templates (Phase 2)
  newsletterTemplates: '/admin/newsletter-templates',
  newsletterTemplateBaselinePrompts: '/admin/newsletter-templates/baseline-prompts',
  newsletterTemplate: (id: number) => `/admin/newsletter-templates/${id}`,
  newsletterTemplatePrompts: (id: number) => `/admin/newsletter-templates/${id}/prompts`,
  newsletterTemplatePersona: (id: number) => `/admin/newsletter-templates/${id}/persona`,
  newsletterTemplateFormat: (id: number) => `/admin/newsletter-templates/${id}/format`,
  newsletterTemplateVisualConfig: (id: number) => `/admin/newsletter-templates/${id}/visual-config`,
  newsletterTemplateTest: (id: number) => `/admin/newsletter-templates/${id}/test`,

  // Domain Prompt Templates (Multi-Domain System)
  domains: '/admin/domains',
  domainSections: (domain: string) => `/admin/domain-sections/${domain}`,
  domainPrompts: (domain: string) => `/admin/domain-prompts/${domain}`,
  domainPrompt: (domain: string, agentType: string) => `/admin/domain-prompts/${domain}/${agentType}`,
  domainPromptHistory: (domain: string, agentType: string) => `/admin/domain-prompts/${domain}/${agentType}/history`,

  // Pattern Detection (Phase 1)
  patterns: '/api/patterns',
  patternCandidates: '/api/patterns/candidates',
  pattern: (id: string) => `/api/patterns/${id}`,
  patternTemporal: (id: string) => `/api/patterns/${id}/temporal`,
  patternTriggers: (id: string) => `/api/patterns/${id}/triggers`,
  patternEnrich: (id: string) => `/api/patterns/${id}/enrich`,
  patternPotentialSuccessors: (id: string) => `/api/patterns/${id}/potential-successors`,
  patternConfidenceDistribution: '/api/patterns/confidence-distribution',
  patternLifecycleDistribution: '/api/patterns/lifecycle-distribution',
  patternImplications: (id: string) => `/api/patterns/${id}/implications`,
  patternImplicationsGenerate: (id: string) => `/api/patterns/${id}/implications/generate`,
  // CUR-464: admin-only list of pattern pairs flagged by the daily merge-candidate scanner
  patternMergeCandidates: '/api/patterns/merge-candidates',
  // CUR-948: merge a duplicate pattern into a survivor (destructive)
  patternMerge: (survivorId: string) => `/api/patterns/${survivorId}/merge`,
  // CUR-949: dismiss a merge-candidate pair as not-a-duplicate
  patternMergeCandidateDismiss: '/api/patterns/merge-candidates/dismiss',
  // CUR-950: batch-merge high-confidence duplicate pairs
  patternMergeCandidateBatchMerge: '/api/patterns/merge-candidates/batch-merge',
  // Plan A — Pattern Clusters
  patternClusters: '/api/patterns/clusters',
  patternClusterProposals: '/api/patterns/clusters/proposals',
  patternCluster: (id: string) => `/api/patterns/clusters/${id}`,
  patternClusterMember: (clusterId: string, patternId: string) => `/api/patterns/clusters/${clusterId}/members/${patternId}`,
  emergingEntities: '/api/emerging-entities',
  emergingEntityAction: (alertId: string, action: string) => `/api/emerging-entities/${alertId}/${action}`,

  // Source Groups
  sourceGroups: '/admin/source-groups',
  sourceGroup: (id: string) => `/admin/source-groups/${id}`,
  sourceGroupMember: (groupId: string, inputSourceId: string) => `/admin/source-groups/${groupId}/members/${inputSourceId}`,
  inputSourceGroups: (sourceId: string) => `/admin/inputs/${sourceId}/groups`,

  // Input Sources (Multi-Platform)
  inputPlatforms: '/admin/inputs/platforms',
  inputSources: '/admin/inputs',
  inputSource: (id: string) => `/admin/inputs/${id}`,
  inputSourceSync: (id: string) => `/admin/inputs/${id}/sync`,
  inputSourceBackfill: (id: string) => `/admin/inputs/${id}/backfill`,
  inputSourceBackfillEstimate: (id: string) => `/admin/inputs/${id}/backfill/estimate`,
  inputSourceBackfills: (id: string) => `/admin/inputs/${id}/backfills`,
  inputSourceThumbnail: (id: string) => `/admin/inputs/${id}/thumbnail`,
  inputSourceSubscribers: (id: string) => `/admin/inputs/${id}/subscribers`,
  adminOrganizations: '/admin/organizations',

  // Creators (Cross-Platform)
  creators: '/admin/creators',
  creator: (name: string) => `/admin/creators/${encodeURIComponent(name)}`,

  // Output Channels (Multi-Platform Publishing)
  outputPlatforms: '/admin/outputs/platforms',
  outputChannels: '/admin/outputs',
  outputChannel: (id: string) => `/admin/outputs/${id}`,
  outputChannelTest: (id: string) => `/admin/outputs/${id}/test`,
  outputPublications: '/admin/outputs/publications',

  // Produced Assets
  assets: '/api/assets',
  asset: (id: string) => `/api/assets/${id}`,

  // AI Costs (Langfuse proxy + direct DB)
  adminCostsSummary: '/admin/costs/summary',
  adminCostsDaily: '/admin/costs/daily',
  adminCostsByModel: '/admin/costs/by-model',
  adminCostsByContent: '/admin/costs/by-content',
  adminCostsItem: '/admin/costs/item',          // + `/${contentId}`
  adminCostsBySource: '/admin/costs/by-source',
  adminCostsWaste: '/admin/costs/waste',
  adminCostsEnvelope: '/admin/costs/envelope',

  // MCP Usage
  adminMcpUsageSummary: '/admin/mcp-usage/summary',
  adminMcpUsageDaily: '/admin/mcp-usage/daily',
  adminMcpUsageByTool: '/admin/mcp-usage/by-tool',

  // Admin Projects (cross-org, super-admin only — CUR-530)
  adminProjects: '/admin/projects',
  adminProject: (id: string) => `/admin/projects/${id}`,


  // Admin Inbound Email (CUR-1052 PR-4 — super-admin only, read-only)
  adminInboundAddresses: '/admin/inbound-addresses',
  adminInboundLog: '/admin/inbound-log',

  // AI Quality (Observability Phase 2 PR-E — super-admin only)
  adminQualitySteps: '/admin/quality/steps',
  adminQualityTrend: '/admin/quality/trend',
  adminQualityRecent: '/admin/quality/recent',
} as const;

// Build full API URLs
export function buildApiUrl(endpoint: string): string {
  return `${config.apiUrl}${endpoint}`;
}

// Log configuration in development
if (isDevelopment) {
  console.log('🔧 App Configuration:', {
    environment: config.environment,
    apiUrl: config.apiUrl,
    version: config.version,
    debug: config.debug,
  });
}
